import React, { useCallback, useEffect, useState, Component } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingBagIcon, BookIcon, InfoIcon, StarIcon, TruckIcon, PackageIcon, HeartIcon, Share2Icon, MailIcon, ImageIcon, TagIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, BellIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ProfileLayout, { ProfileData } from '../components/Profile/ProfileLayout';
import { AboutTab, ReviewsTab } from '../components/Profile/ProfileTabs';
import ProductCard from '../components/Products/ProductCard';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { useDarkMode } from '../context/DarkModeContext';
import { FulfillmentTransparencyCard } from '../components/Vendors/FulfillmentTransparencyCard';
import { VendorUpdatesFeed } from '../components/Vendors/VendorUpdatesFeed';
import { mockFulfillmentData, mockVendorUpdates } from '../data/fulfillmentData';
// --- Mock Data ---
const vendorData = {
  id: '1',
  name: 'Literary Apparel Co.',
  slug: 'literary-apparel-co',
  avatar: 'https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1581318694548-0fb6e47fe59b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Premium book-themed clothing and accessories for literary enthusiasts. We collaborate directly with authors to create exclusive, high-quality merchandise that brings your favorite stories to life.',
  location: 'Portland, OR',
  rating: 4.8,
  reviewCount: 245,
  joinDate: '2021',
  categories: ['Clothing', 'Accessories', 'Home Decor'],
  shippingInfo: {
    countries: ['United States', 'Canada', 'UK', 'Australia'],
    processingTime: '3-5 business days',
    shippingTime: '5-7 business days'
  },
  policies: {
    returns: 'Accepted within 30 days',
    exchanges: 'Accepted within 45 days',
    customization: 'Available for select items'
  },
  social: {
    website: 'https://literaryapparel.co',
    instagram: '@literaryapparel',
    twitter: '@literaryapparel',
    email: 'hello@literaryapparel.co'
  }
};
// Map to ProfileLayout Data Structure
const profileData: ProfileData = {
  id: vendorData.id,
  name: vendorData.name,
  role: 'Official Merch Partner',
  avatar: vendorData.avatar,
  coverImage: vendorData.banner,
  status: 'Open',
  queueStatus: 'open',
  slotsAvailable: 450,
  maxSlots: 500,
  estimatedWait: '3-5 days',
  rating: vendorData.rating,
  location: vendorData.location,
  bio: vendorData.description,
  specialties: vendorData.categories,
  stats: [{
    label: 'Rating',
    value: vendorData.rating
  }, {
    label: 'Sales',
    value: '12.5k'
  }, {
    label: 'Products',
    value: '142'
  }, {
    label: 'Since',
    value: vendorData.joinDate
  }],
  social: vendorData.social
};
const products = [{
  id: '1',
  title: 'Silent Echo T-Shirt',
  vendor: 'Literary Apparel Co.',
  image: 'https://images.unsplash.com/photo-1576566588028-4147f3842f27?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 24.99,
  relatedBook: 'The Silent Echo',
  isOfficialMerch: true,
  category: 'clothing',
  licensor: 'elena richards'
}, {
  id: '2',
  title: 'Echo Series Bookmark Set',
  vendor: 'Literary Apparel Co.',
  image: 'https://images.unsplash.com/photo-1544947987-f6c4fa25fa4a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 12.99,
  relatedBook: 'The Silent Echo',
  isOfficialMerch: true,
  category: 'accessories',
  licensor: 'elena richards'
}, {
  id: '3',
  title: 'Book Lover Tote Bag',
  vendor: 'Literary Apparel Co.',
  image: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 29.99,
  isOfficialMerch: false,
  category: 'accessories',
  licensor: null
}, {
  id: '4',
  title: 'Lost City Map Print',
  vendor: 'Literary Apparel Co.',
  image: 'https://images.unsplash.com/photo-1582574501058-6f3b5c0c5e6b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 29.99,
  relatedBook: 'The Lost City',
  isOfficialMerch: true,
  category: 'home decor',
  licensor: 'david williams'
}, {
  id: '5',
  title: 'Beyond the Veil T-Shirt',
  vendor: 'Literary Apparel Co.',
  image: 'https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 26.99,
  relatedBook: 'Beyond the Veil',
  isOfficialMerch: true,
  category: 'clothing',
  licensor: 'marcus chen'
}, {
  id: '6',
  title: 'Veil Series Art Print',
  vendor: 'Literary Apparel Co.',
  image: 'https://images.unsplash.com/photo-1582574501058-6f3b5c0c5e6b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 19.99,
  relatedBook: 'Beyond the Veil',
  isOfficialMerch: true,
  category: 'home decor',
  licensor: 'marcus chen'
}, {
  id: '7',
  title: 'Whispers Candle Set',
  vendor: 'Literary Apparel Co.',
  image: 'https://images.unsplash.com/photo-1603006905003-be475563f01a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 32.99,
  relatedBook: 'Whispers in the Dark',
  isOfficialMerch: true,
  category: 'home decor',
  licensor: 'sarah johnson'
}];
const reviews = [{
  id: 'r1',
  clientName: 'Jessica M.',
  clientRole: 'Verified Buyer',
  clientAvatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100',
  rating: 5,
  comment: 'The quality of the t-shirt is amazing! The print is soft and the fabric is so comfortable. Definitely buying more.',
  date: 'Oct 2023',
  projectType: 'Clothing',
  completionTime: 'Shipped in 3 days'
}, {
  id: 'r2',
  clientName: 'Michael T.',
  clientRole: 'Verified Buyer',
  clientAvatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100',
  rating: 5,
  comment: 'Love the bookmark set. It came in beautiful packaging and arrived earlier than expected.',
  date: 'Sep 2023',
  projectType: 'Accessories',
  completionTime: 'Shipped in 2 days'
}, {
  id: 'r3',
  clientName: 'Sarah L.',
  clientRole: 'Verified Buyer',
  clientAvatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100',
  rating: 4,
  comment: 'Great tote bag, very sturdy. Took a little longer to arrive than I hoped, but worth the wait.',
  date: 'Aug 2023',
  projectType: 'Accessories',
  completionTime: 'Shipped in 7 days'
}];
// --- Sub-Components ---
const ProductsTab = ({
  products
}: {
  products: typeof products;
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [activeCategory, setActiveCategory] = useState('all');
  const categories = ['All', 'Clothing', 'Accessories', 'Home Decor'];
  const filteredProducts = activeCategory === 'all' ? products : products.filter(p => p.category === activeCategory.toLowerCase());
  return <div className="space-y-8" data-id="element-16934" data-mp="3s4mccx12qa18">
      {/* Category Filter */}
      <div className="flex flex-wrap gap-2" data-id="element-16935" data-mp="3s675qjbnjmxi">
        {categories.map(category => <button key={category} onClick={() => setActiveCategory(category.toLowerCase())} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCategory === category.toLowerCase() ? `${rt.bgMedium} ${rt.textPrimary}` : `${dk.card} ${dk.textSecondary} hover:${dk.textPrimary}`}`} data-id="element-16936" data-mp="3n5t8lwbl9xe9">
            {category}
          </button>)}
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-16937" data-mp="npd3wgsutml3">
        {filteredProducts.map(product => <ProductCard key={product.id} {...product} data-id="element-16938" data-mp="jn9hwy8k6zlk" />)}
      </div>

      {filteredProducts.length === 0 && <div className={`text-center py-12 rounded-xl border-2 border-dashed ${dk.border}`} data-id="element-16939" data-mp="2d8uv89kemdtz">
          <ShoppingBagIcon className={`mx-auto h-12 w-12 mb-4 ${dk.textMuted}`} data-id="element-16940" data-mp="2i40gr79w9qhj" />
          <h3 className={`text-lg font-medium ${dk.textHeading}`} data-id="element-16941" data-mp="2n34qq04n7s8x">
            No products found
          </h3>
          <p className={dk.textSecondary} data-id="element-16942" data-mp="2vxjdenri6522">
            Try adjusting your category filter.
          </p>
        </div>}
    </div>;
};
const LicensedTab = ({
  products
}: {
  products: typeof products;
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [activeLicensor, setActiveLicensor] = useState('all');
  const licensors = [{
    id: 'all',
    label: 'All Collections'
  }, {
    id: 'elena richards',
    label: 'Elena Richards',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80'
  }, {
    id: 'marcus chen',
    label: 'Marcus Chen',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80'
  }, {
    id: 'sarah johnson',
    label: 'Sarah Johnson',
    avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80'
  }, {
    id: 'david williams',
    label: 'David Williams',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80'
  }];
  const filteredProducts = activeLicensor === 'all' ? products.filter(p => p.licensor) : products.filter(p => p.licensor === activeLicensor);
  return <div className="space-y-8" data-id="element-16943" data-mp="2sa1pllu1iih3">
      {/* Licensor Filter */}
      <div className="flex flex-wrap gap-3" data-id="element-16944" data-mp="2sbcs7ibr1k6m">
        {licensors.map(licensor => <button key={licensor.id} onClick={() => setActiveLicensor(licensor.id)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors border ${activeLicensor === licensor.id ? `${rt.bgMedium} ${rt.textPrimary} border-transparent` : `${dk.card} ${dk.textSecondary} border-transparent hover:${dk.textPrimary}`}`} data-id="element-16945" data-mp="2na6ut77598qr">
            {licensor.avatar && <img src={licensor.avatar} alt="" className="w-5 h-5 rounded-full object-cover" data-id="element-16946" data-mp="1e89ywbadaq3w" />}
            {licensor.label}
          </button>)}
      </div>

      {/* Info Banner */}
      <div className={`p-6 rounded-xl border ${dk.isDark ? 'bg-indigo-900/20 border-indigo-800' : 'bg-indigo-50 border-indigo-100'}`} data-id="element-16947" data-mp="1o5xxxdf0ajrv">
        <div className="flex items-start gap-4" data-id="element-16948" data-mp="3pxay5p6143hl">
          <div className={`p-3 rounded-full ${dk.isDark ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-600'}`} data-id="element-16949" data-mp="3py2vb4f9y99z">
            <BookIcon className="w-6 h-6" data-id="element-16950" data-mp="20g5pkbs29qmt" />
          </div>
          <div data-id="element-16951" data-mp="20is44vddd6ej">
            <h3 className={`text-lg font-bold mb-1 ${dk.textHeading}`} data-id="element-16952" data-mp="25fsal7e4sb66">
              Official Merchandise
            </h3>
            <p className={`text-sm ${dk.textSecondary}`} data-id="element-16953" data-mp="1qrnri2b61xmv">
              All items in these collections are officially licensed and
              approved by the authors. A portion of every sale goes directly to
              support the creators of the stories you love.
            </p>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-16954" data-mp="1lponcq7lqwov">
        {filteredProducts.map(product => <ProductCard key={product.id} {...product} data-id="element-16955" data-mp="1cquth7lnokf0" />)}
      </div>

      {filteredProducts.length === 0 && <div className="text-center py-12" data-id="element-16956" data-mp="325rlh8hr7lrx">
          <p className={dk.textMuted} data-id="element-16957" data-mp="323ybbj7xmjf6">
            No licensed items found for this collection.
          </p>
        </div>}
    </div>;
};
type GalleryFilter = 'posted' | 'tagged';
interface VendorGalleryImage {
  id: string;
  src: string;
  alt: string;
  type: 'posted' | 'tagged';
  caption?: string;
  credit?: string;
}
const vendorGalleryImages: VendorGalleryImage[] = [{
  id: '1',
  src: 'https://images.unsplash.com/photo-1503342394128-c104d54dba01?w=600&q=80',
  alt: 'T-shirt product shot',
  type: 'posted',
  caption: 'New Silent Echo tee — just dropped 🖤'
}, {
  id: '2',
  src: 'https://images.unsplash.com/photo-1544947987-f6c4fa25fa4a?w=400&q=80',
  alt: 'Bookmark set',
  type: 'posted',
  caption: 'Echo Series bookmark set'
}, {
  id: '3',
  src: 'https://images.unsplash.com/photo-1576566588028-4147f3842f27?w=700&q=80',
  alt: 'Lifestyle shirt',
  type: 'tagged',
  credit: '@bookishstyle',
  caption: 'Obsessed with this fit 😍'
}, {
  id: '4',
  src: 'https://images.unsplash.com/photo-1544967082-d9d25d867d66?w=500&q=80',
  alt: 'Tote bag in use',
  type: 'tagged',
  credit: '@readinglife',
  caption: 'My new everyday bag 📚'
}, {
  id: '5',
  src: 'https://images.unsplash.com/photo-1582574501058-6f3b5c0c5e6b?w=600&q=80',
  alt: 'Art print on wall',
  type: 'posted',
  caption: 'Lost City Map Print — limited run'
}, {
  id: '6',
  src: 'https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?w=400&q=80',
  alt: 'Beyond the Veil tee',
  type: 'tagged',
  credit: '@marcusfan',
  caption: 'This shirt is everything'
}, {
  id: '7',
  src: 'https://images.unsplash.com/photo-1603006905003-be475563f01a?w=600&q=80',
  alt: 'Candle set',
  type: 'posted',
  caption: 'Whispers candle set — smells incredible 🕯️'
}, {
  id: '8',
  src: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=500&q=80',
  alt: 'Sweatshirt',
  type: 'tagged',
  credit: '@cozybookworm',
  caption: 'Weekend vibes sorted'
}, {
  id: '9',
  src: 'https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=700&q=80',
  alt: 'Apparel flat lay',
  type: 'posted',
  caption: 'Fall collection preview 🍂'
}];
const VendorGalleryTab = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const [filter, setFilter] = useState<GalleryFilter>('posted');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const filtered = vendorGalleryImages.filter(img => img.type === filter);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prev = useCallback(() => setLightboxIndex(i => i === null ? null : (i - 1 + filtered.length) % filtered.length), [filtered.length]);
  const next = useCallback(() => setLightboxIndex(i => i === null ? null : (i + 1) % filtered.length), [filtered.length]);
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    if (lightboxIndex !== null) {
      document.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [lightboxIndex, closeLightbox, prev, next]);
  const selectedImage = lightboxIndex !== null ? filtered[lightboxIndex] : null;
  return <>
      <div data-id="element-16958" data-mp="9i3e415xb2d6">
        <div className="flex items-center justify-between mb-6" data-id="element-16959" data-mp="9jegpxnmu42p">
          <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-16960" data-mp="ef399h18ikpi">Gallery</h2>
          <div className={`flex rounded-xl p-1 gap-1 ${dk.surfaceMuted}`} data-id="element-16961" data-mp="3vxfny2oepkmh">
            {(['posted', 'tagged'] as GalleryFilter[]).map(f => <button key={f} onClick={() => {
            setFilter(f);
            setLightboxIndex(null);
          }} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${filter === f ? `${dk.card} ${dk.textPrimary} shadow-sm` : `${dk.textMuted} hover:${dk.textPrimary}`}`} data-id="element-16962" data-mp="4os6vyx78jkq">
                {f === 'tagged' && <TagIcon className="w-3.5 h-3.5" data-id="element-16963" data-mp="y26g0yp9k3sv" />}
                {f === 'posted' ? 'Our Posts' : 'Tagged In'}
              </button>)}
          </div>
        </div>

        {filtered.length > 0 ? <div className="columns-2 sm:columns-3 lg:columns-4 gap-3" data-id="element-16964" data-mp="o3qpawu5ewjw">
            {filtered.map((img, idx) => <button key={img.id} onClick={() => setLightboxIndex(idx)} className="break-inside-avoid mb-3 block w-full rounded-xl overflow-hidden group relative focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500" data-id="element-16965" data-mp="jd1x1fan9vjp" data-mpi={idx}>
                <img src={img.src} alt={img.alt} className="w-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-16966" data-mp="5dsgufmfv1fq" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-200 flex items-end p-3" data-id="element-16967" data-mp="2c0q35xxorlj4">
                  <p className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200 line-clamp-2 text-left" data-id="element-16968" data-mp="2gz1g4hja2c5k">
                    {img.caption}
                    {img.credit && <span className="block text-white/70 mt-0.5" data-id="element-16969" data-mp="1tba47f1ng14t">
                        {img.credit}
                      </span>}
                  </p>
                </div>
              </button>)}
          </div> : <div className={`text-center py-16 rounded-2xl border-2 border-dashed ${dk.border}`} data-id="element-16970" data-mp="22aoqs3ep3rk1">
            <ImageIcon className={`h-12 w-12 mx-auto mb-3 opacity-40 ${dk.textMuted}`} data-id="element-16971" data-mp="274j7qz2x1sff" />
            <p className={dk.textMuted} data-id="element-16972" data-mp="1emduzenuwi2m">No images yet</p>
          </div>}
      </div>

      <AnimatePresence data-id="element-16973" data-mp="7z34h88dsj6w">
        {selectedImage && <>
            <motion.div key="lb-backdrop" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 bg-black/90 z-50" onClick={closeLightbox} data-id="element-16974" data-mp="7zbjb5o2dykq" />
            <motion.div key="lb-panel" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-16975" data-mp="lzdxagt947up">
              <div className="relative max-w-4xl w-full pointer-events-auto" onClick={e => e.stopPropagation()} data-id="element-16976" data-mp="1ocf7u2e1oxvw">
                <img src={selectedImage.src} alt={selectedImage.alt} className="w-full max-h-[80vh] object-contain rounded-xl shadow-2xl" data-id="element-16977" data-mp="1fayze06sj5ub" />
                {(selectedImage.caption || selectedImage.credit) && <div className="mt-3 text-center" data-id="element-16978" data-mp="1abcnbu2p7vn3">
                    {selectedImage.caption && <p className="text-white text-sm font-medium" data-id="element-16979" data-mp="wk7ubatu61lq">
                        {selectedImage.caption}
                      </p>}
                    {selectedImage.credit && <p className="text-white/60 text-xs mt-0.5" data-id="element-16980" data-mp="ml8l9r55s3y7">
                        {selectedImage.credit}
                      </p>}
                  </div>}
                <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full" data-id="element-16981" data-mp="qoy3zrf284r5">
                  {(lightboxIndex ?? 0) + 1} / {filtered.length}
                </div>
                <button onClick={closeLightbox} className="absolute top-3 right-3 p-2 rounded-full bg-black/60 backdrop-blur-sm text-white hover:bg-black/80 transition-colors" data-id="element-16982" data-mp="gsbj52mqhdp4">
                  <XIcon className="w-5 h-5" data-id="element-16983" data-mp="19gqvfhehdhwr" />
                </button>
                {filtered.length > 1 && <>
                    <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-black/60 backdrop-blur-sm text-white hover:bg-black/80 transition-colors" data-id="element-16984" data-mp="zqffewhivi9i">
                      <ChevronLeftIcon className="w-5 h-5" data-id="element-16985" data-mp="1sdiepqu0vx0t" />
                    </button>
                    <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-black/60 backdrop-blur-sm text-white hover:bg-black/80 transition-colors" data-id="element-16986" data-mp="1x74eiif386f7">
                      <ChevronRightIcon className="w-5 h-5" data-id="element-16987" data-mp="3qos8b70g0r37" />
                    </button>
                  </>}
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
};
const VendorProfile = () => {
  // Route is /vendor/:id
  const {
    id: slug
  } = useParams<{
    id: string;
  }>();
  const [activeTab, setActiveTab] = useState('products');
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const vendorFulfillment = mockFulfillmentData['1'];
  const vendorUpdates = mockVendorUpdates['1'] || [];
  const tabs = [{
    id: 'products',
    label: 'Shop All',
    icon: ShoppingBagIcon,
    count: products.length
  }, {
    id: 'licensed',
    label: 'Collections',
    icon: BookIcon,
    count: 4
  }, {
    id: 'updates',
    label: 'Updates',
    icon: BellIcon,
    count: vendorUpdates.length
  }, {
    id: 'gallery',
    label: 'Gallery',
    icon: ImageIcon
  }, {
    id: 'about',
    label: 'About & Policies',
    icon: InfoIcon
  }, {
    id: 'reviews',
    label: 'Reviews',
    icon: StarIcon,
    count: reviews.length
  }];
  // Map vendor data to AboutTab props
  const processSteps = [{
    title: 'Order Placed',
    description: 'Confirmation email sent immediately'
  }, {
    title: 'Processing',
    description: vendorData.shippingInfo.processingTime
  }, {
    title: 'Quality Check',
    description: 'Hand-inspected for perfection'
  }, {
    title: 'Shipping',
    description: vendorData.shippingInfo.shippingTime
  }, {
    title: 'Delivery',
    description: 'Tracked shipping to your door'
  }];
  const willDo = [...vendorData.shippingInfo.countries.map(c => `Ships to ${c}`), 'Custom Orders', 'Bulk Discounts', 'Gift Wrapping Available'];
  const wontDo = ['Unverified Addresses', 'Returns on Custom Items', 'Rush Shipping (Standard Only)'];
  const actionButtons = <>
      <button className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-colors shadow-lg flex items-center gap-2 text-white ${rt.primaryButton}`} data-id="element-16988" data-mp="1x5fcqdrkn375">
        <ShoppingBagIcon className="w-4 h-4" data-id="element-16989" data-mp="1o3fuji4tlt2m" />
        Visit Store
      </button>
      <button className={`px-6 py-2.5 rounded-lg text-sm font-bold border transition-colors flex items-center gap-2 ${dk.card}`} data-id="element-16990" data-mp="1jbo5jc1bbvel">
        <MailIcon className="w-4 h-4" data-id="element-16991" data-mp="1eas0uw950584" />
        Contact
      </button>
    </>;
  return <ProfileLayout profile={profileData} tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} actionButtons={actionButtons} data-id="element-16992" data-mp="19fl0nyf2tiny">
      {vendorFulfillment && <FulfillmentTransparencyCard fulfillment={vendorFulfillment} data-id="element-16993" data-mp="10dv7oo5jqbo2" />}

      {activeTab === 'products' && <ProductsTab products={products} data-id="element-16994" data-mp="30hfm8dtr3pz6" />}

      {activeTab === 'licensed' && <LicensedTab products={products} data-id="element-16995" data-mp="34geqdw7nripk" />}

      {activeTab === 'updates' && <VendorUpdatesFeed updates={vendorUpdates} data-id="element-16996" data-mp="2qqbb5p774p9m" />}

      {activeTab === 'gallery' && <VendorGalleryTab data-id="element-16997" data-mp="2vocv0ccg4sm5" />}

      {activeTab === 'about' && <AboutTab profile={profileData} skills={vendorData.categories} // Mapping categories to skills
    tools={['Screen Printing', 'Embroidery', 'Digital Print']} // Mock tools
    processSteps={processSteps} willDo={willDo} wontDo={wontDo} funFacts={['Founded by book lovers for book lovers', 'Based in the Pacific Northwest', 'Uses eco-friendly packaging']} data-id="element-16998" data-mp="2vmi6ko1yuidb" />}

      {activeTab === 'reviews' && <ReviewsTab reviews={reviews} data-id="element-16999" data-mp="aydng3zbclk2" />}
    </ProfileLayout>;
};
export default VendorProfile;